import { useEffect, useRef, useState, type ReactNode } from "react";
import { useCursorEffect } from "@/machines/context/cursor-store";
import { useLiveTimelineDomain } from "@/machines/components/common/timeline-editor/hooks/useLiveTimelineDomain";
import { LIVE_CURSOR_ANCHOR } from "@/map/hooks/useRealtimeWindow";
import { useVideoLibraryStore } from "@/machines/stores/useVideoLibraryStore";
import { liveAnchorFraction, liveBufferMs } from "@/machines/utils/live-playback-buffer";
import {
  LiveTimelineDomainContext,
  type LiveTimelineDomain,
} from "./live-timeline-domain-context";

/** How often the shared domain advances — matches the live view's own tick. */
const SAMPLE_MS = 1000;

/**
 * ONE live domain and ONE cursor for the whole stacked timeline.
 *
 * The stack draws a single time axis under all its rows, but each row used to
 * compute its own live domain from its OWN cursor store — and only the active
 * row's cursor is driven by video playback. Different anchors produced different
 * `[min, max]`, so the same instant landed at a different x in every row and the
 * playheads never lined up, all beneath an axis that claimed they shared a
 * window. Computing it once here makes that claim true.
 *
 * Must be rendered INSIDE the active machine's provider: that is where the
 * cursor the video drives lives. Rows re-declare their own cursor store, but this
 * context passes straight through them untouched.
 *
 * The cursor is mirrored through a ref and sampled on a timer rather than read
 * with `useSelectedTime`: during playback it changes ~30×/sec, and re-rendering
 * the whole stack at frame rate is exactly the render storm the cursor store was
 * split out to prevent. Sampling costs one re-render per second — the same
 * cadence each row already paid individually.
 */
/**
 * Anchor steps. The buffer moves with every clip that lands; re-deriving an
 * exact anchor from it would nudge the whole domain a few pixels each time,
 * which reads as drift. Rounding to 5% means the view only ever moves when the
 * pipeline has genuinely changed character.
 */
const ANCHOR_STEP = 0.05;

const LiveTimelineDomainProvider = ({
  enabled,
  spanMs,
  paused = false,
  deviceMxid,
  children,
}: {
  enabled: boolean;
  spanMs: number;
  /** Freeze the advance (edit mode / alert review), matching the rows' own rule. */
  paused?: boolean;
  /**
   * The active machine, used to derive where the cursor sits across the view
   * from how far behind live its playback actually is.
   */
  deviceMxid?: string;
  children: ReactNode;
}) => {
  const cursorRef = useRef<number | null>(null);
  useCursorEffect((snap) => {
    cursorRef.current = snap.selectedTime;
  });

  const [cursorMs, setCursorMs] = useState<number | null>(null);
  /**
   * Where the cursor sits across the view.
   *
   * The constant 0.9 reserves one minute of a ten-minute view for the cursor's
   * lag, but live playback deliberately sits 90–120 s behind the footage edge
   * (see `live-playback-buffer`) — so the cursor was further left than its own
   * anchor, `useLiveTimelineDomain` gave up anchoring, and the window followed
   * the wall clock instead while the cursor slid away from it. Deriving the
   * anchor from the same buffer playback uses keeps the two agreeing at any
   * span. Sampled on the existing 1 Hz tick — no extra subscription, and the
   * store is read imperatively so arriving clips never re-render this.
   */
  const [anchorFraction, setAnchorFraction] = useState(LIVE_CURSOR_ANCHOR);
  useEffect(() => {
    if (!enabled) {
      setCursorMs(null);
      return;
    }
    const sample = () => {
      setCursorMs(cursorRef.current);
      if (!deviceMxid) return;
      const bufferMs = liveBufferMs(useVideoLibraryStore.getState().getClipArrivals(deviceMxid));
      const raw = liveAnchorFraction(bufferMs, spanMs);
      setAnchorFraction((prev) =>
        // Hysteresis, not rounding: quantising alone leaves a buffer hovering on
        // a step boundary flipping 0.85 ↔ 0.80 every second, which moves the
        // whole domain half a minute back and forth on a 10-minute view. The
        // raw value has to travel a full step away from where we already sit
        // before the anchor moves at all.
        Math.abs(raw - prev) < ANCHOR_STEP ? prev : Math.round(raw / ANCHOR_STEP) * ANCHOR_STEP,
      );
    };
    sample();
    const id = setInterval(sample, SAMPLE_MS);
    return () => clearInterval(id);
  }, [enabled, deviceMxid, spanMs]);

  const domain = useLiveTimelineDomain({
    enabled,
    spanMs,
    paused,
    anchorMs: cursorMs,
    anchorFraction,
  });

  const value = useRef<LiveTimelineDomain>({ domain, cursorMs });
  // Rebuild the object only when a value actually changed, so a sample that
  // lands on the same millisecond doesn't re-render every consuming row.
  if (
    value.current.cursorMs !== cursorMs ||
    value.current.domain?.min !== domain?.min ||
    value.current.domain?.max !== domain?.max
  ) {
    value.current = { domain, cursorMs };
  }

  return (
    <LiveTimelineDomainContext.Provider value={value.current}>
      {children}
    </LiveTimelineDomainContext.Provider>
  );
};

export default LiveTimelineDomainProvider;
